import { useModal } from "@/Contexts";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { getFileExtension } from "@/Utils/getFileExtension";
import { trpc } from "@/Utils/trpc";
import ModalContainer from "@/components/modalContainer";

import { useCallback, useContext, useMemo, useState } from "react";

import { t } from "i18next";
import type { FILE_LOCATION_TYPE } from "server/bucketApi/library/constants.js";
import { twMerge } from "tailwind-merge";

import { LibraryPageContext } from "../../context";
import { FileStatus } from "../../types";
import { isSafeFileName } from "../../utils";
import { FileStatusSelect } from "./components/fileStatusSelect";
import { FoldersSelect } from "./components/foldersSelect";
import { UploadZone } from "./components/uploadZone";

const fileSizeLimit = 50 * 1024 * 1024; // 50 MB

export const UploadFileModal: React.FC = () => {
  const { hideModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();
  const trpcUtils = trpc.useUtils();
  const { currentTerminal } = useTerminalsState();
  const { allFiles, refetchFiles } = useContext(LibraryPageContext);

  const [file, setFile] = useState<File | null>(null);
  const [hasFileError, setHasFileError] = useState<boolean>(false);
  const [name, setName] = useState("");
  const [selectedFolderName, setSelectedFolderName] = useState<string>("");
  const [fileStatus, setFileStatus] = useState<FileStatus>(
    FileStatus.PUBLISHED,
  );
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);

  const isNameValid = useMemo(() => isSafeFileName(name), [name]);

  const onFileSelected = useCallback((selectedFile: File | null) => {
    setFile(selectedFile);
    if (selectedFile) {
      setName(selectedFile.name.replace(/\.[^./]+$/, ""));
    } else {
      setName("");
    }
  }, []);

  const onSubmit = useCallback(async () => {
    if (!file || !isNameValid) return;

    setIsUploadingFile(true);
    const { url } = await trpcUtils.library.getPublishedDocumentUploadUrl.fetch(
      {
        terminalId: currentTerminal?.id!,
        filename: `${name}.${getFileExtension(file.name)}`,
        folderName: selectedFolderName,
        type: fileStatus as string as FILE_LOCATION_TYPE,
      },
    );

    if (allFiles.find((f) => f.name === name)) {
      setIsUploadingFile(false);
      setGlobalAlert({
        type: "error",
        message: "errors:documentAlreadyExists",
      });
      return;
    }

    if (file.size > fileSizeLimit) {
      setIsUploadingFile(false);
      setGlobalAlert({
        type: "error",
        message: t("errors:fileTooLarge", {
          size: fileSizeLimit / (1024 * 1024),
        }),
      });
      return;
    }

    const uploadResponse = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type, // MUST match what was signed
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      setIsUploadingFile(false);
      setGlobalAlert({
        type: "error",
        message: "errors:uploadingDocumentFailed",
      });

      throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
    }

    refetchFiles();
    setIsUploadingFile(false);
    hideModal();
  }, [
    file,
    isNameValid,
    trpcUtils.library.getPublishedDocumentUploadUrl,
    currentTerminal?.id,
    allFiles,
    refetchFiles,
    setGlobalAlert,
    selectedFolderName,
    hideModal,
    fileStatus,
    name,
  ]);

  return (
    <ModalContainer
      title={t("libraryPage:fileUploadModalTitle")}
      onConfirmText={t("libraryPage:fileUploadModalConfirm")}
      onConfirmType="black"
      onConfirm={onSubmit}
      onCancelType="white"
      onCancel={hideModal}
      className="w-[520px] max-w-full"
      onConfirmDisabled={!file || !isNameValid}
      onConfirmLoading={isUploadingFile}
    >
      <div className="flex flex-col gap-[16px]">
        <UploadZone
          hasFileError={hasFileError}
          setHasFileError={setHasFileError}
          file={file}
          setFile={onFileSelected}
        />

        <label className="text-grey6 flex flex-col gap-[5px] text-[12px] font-medium">
          {t("libraryPage:fileUploadName")}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("libraryPage:fileUploadNamePlaceholder")}
            className={twMerge(
              "rounded border px-[11px] py-[9px] text-[14px] text-[#1D1C1C] outline-none",
              !isNameValid && name.length ? "border-red-500" : "border-grey3",
            )}
          />
          {!isNameValid && !!name.length && (
            <span className="font-normal text-red-500">
              {t("libraryPage:fileUploadNameInvalidChars")}
            </span>
          )}
        </label>

        <FileStatusSelect
          fileStatus={fileStatus}
          setFileStatus={setFileStatus}
        />

        {fileStatus === FileStatus.PUBLISHED && (
          <FoldersSelect
            selectedFolderName={selectedFolderName}
            setSelectedFolderName={setSelectedFolderName}
          />
        )}
      </div>
    </ModalContainer>
  );
};
